import { useState } from 'react';
import { Bell, CheckCircle, AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { AnimateIn } from '../../shared/ui/AnimateIn';
import { Button } from '../../shared/ui/Button';
import { Skeleton } from '../../shared/ui/Skeleton';
import { EmptyState } from '../../shared/ui/EmptyState';
import { ErrorState } from '../../shared/ui/ErrorState';
import { useToast } from '../../shared/ui/Toast';
import { useNotifications, useMarkRead, useMarkAllRead } from '../../features/notifications/hooks';
import { usePageMeta } from '../../shared/hooks/usePageMeta';
import type { Notification } from '../../shared/types/app';

const TYPE_ICON: Record<Notification['type'], typeof Bell> = {
  info: Info,
  success: CheckCircle,
  warning: AlertTriangle,
  danger: AlertCircle,
};

const TYPE_COLOR: Record<Notification['type'], string> = {
  info:    'text-info',
  success: 'text-success',
  warning: 'text-warning',
  danger:  'text-danger',
};

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function dayLabel(date: Date): string {
  const now = new Date();
  if (isSameDay(date, now)) return 'Today';
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (isSameDay(date, yesterday)) return 'Yesterday';
  return date.toLocaleDateString('en-JM', { weekday: 'long', month: 'long', day: 'numeric' });
}

function groupByDate(notifications: Notification[]) {
  const groups: Record<string, Notification[]> = {};
  for (const n of notifications) {
    const day = dayLabel(new Date(n.createdAt));
    if (!groups[day]) groups[day] = [];
    groups[day].push(n);
  }
  return groups;
}

export function NotificationsPage() {
  usePageMeta('Notifications');
  const [showRead, setShowRead] = useState(false);
  const { data = [], isLoading, isError, refetch } = useNotifications({ unreadOnly: !showRead });
  const markRead = useMarkRead();
  const markAllRead = useMarkAllRead();
  const { toast } = useToast();

  const unreadCount = data.filter((n) => !n.read).length;
  const groups = groupByDate(data);

  const handleMarkAll = async () => {
    try {
      await markAllRead.mutateAsync();
      toast('All notifications marked as read.', 'success');
    } catch {
      toast('Failed to mark all as read.', 'error');
    }
  };

  return (
    <div className="flex flex-col max-w-2xl mx-auto w-full">

      {/* Header */}
      <div className="pb-5 flex items-end justify-between">
        <div>
          <p className="text-xs font-bold text-muted uppercase tracking-widest mb-1">Customer</p>
          <h1 className="text-2xl font-black text-text">Notifications</h1>
          <p className="text-xs text-muted mt-1">
            {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up'}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowRead((v) => !v)}
            className="!text-muted normal-case !tracking-normal !border-2 !border-line rounded-xl hover:!text-text hover:!border-text/30 hover:!bg-transparent"
          >
            {showRead ? 'Hide read' : 'Show all'}
          </Button>
          {unreadCount > 0 && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleMarkAll}
              disabled={markAllRead.isPending}
              className="!text-accent normal-case !tracking-normal !border-2 !border-accent/30 rounded-xl hover:!bg-accent hover:!text-black"
            >
              Mark all read
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <div key={i} className="flex items-start gap-3 rounded-xl px-3 py-3">
              <Skeleton className="h-8 w-8 rounded-full flex-shrink-0" />
              <div className="flex-1 min-w-0 flex flex-col gap-2">
                <Skeleton className="h-3.5 w-2/3" />
                <Skeleton className="h-3 w-1/3" />
              </div>
            </div>
          ))}
        </div>
      ) : isError ? (
        <div className="py-12">
          <ErrorState onRetry={() => refetch()} />
        </div>
      ) : data.length === 0 ? (
        <div className="py-20">
          <EmptyState
            title="No notifications"
            description="You're all caught up. New updates will appear here."
            icon={<Bell size={24} />}
          />
        </div>
      ) : (
        <div className="flex flex-col gap-6 pt-5">
          {Object.entries(groups).map(([day, items], gi) => (
            <AnimateIn key={day} animation="fade-up" delay={gi * 80}>
            <div>
              <p className="text-[11px] font-bold text-muted uppercase tracking-widest mb-2">{day}</p>
              <div className="rounded-xl overflow-hidden bg-surface-raised/40">
                {items.map((n) => {
                  const Icon = TYPE_ICON[n.type];
                  return (
                    <div
                      key={n.id}
                      className={`flex items-start gap-3 px-4 py-3.5 transition-colors hover:bg-surface-raised ${n.read ? 'opacity-50' : ''}`}
                    >
                      <div className={`mt-0.5 flex-shrink-0 ${TYPE_COLOR[n.type]}`}>
                        <Icon size={16} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-text">{n.title}</p>
                        <p className="text-xs text-text-secondary mt-0.5 leading-relaxed">{n.body}</p>
                        <p className="text-xs text-muted mt-1">
                          {new Date(n.createdAt).toLocaleTimeString('en-JM', {
                            hour: '2-digit', minute: '2-digit',
                          })}
                        </p>
                      </div>
                      {!n.read && (
                        <Button
                          variant="ghost"
                          onClick={() => markRead.mutate(n.id)}
                          className="flex-shrink-0 !text-xs !text-accent normal-case !tracking-normal !px-0 hover:underline hover:!bg-transparent"
                        >
                          Mark read
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            </AnimateIn>
          ))}
        </div>
      )}
    </div>
  );
}
